import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FilesService, IncaricoService, ServizioFiduciarioService } from '../services/apiService';
import { Incarico } from '../types';
import { useToast } from '../components/ToastContext';
import { checkExifDate } from '../utils/utils';
import { safeAction } from '../utils/safeAction';
import { captureClientError, clientErrorLogger } from '../utils/clientErrorLogger';

// Polling dei media in elaborazione: ogni 5 secondi, al massimo per 10 minuti.
const PROCESSING_POLL_MS = 5_000;
const PROCESSING_POLL_MAX_MS = 10 * 60 * 1_000;

// Item nella coda di upload (prima che parta il chunked upload)
export interface UploadQueueItem {
    file: File;
    category: string;
    preview: string;
    manualDate?: string;
}

// Aggiunge un giorno a "YYYY-MM-DD" senza passare da toISOString (UTC).
// Serve per i turni notturni, che finiscono il giorno dopo dataTurno.
const addOneDayStr = (s: string): string => {
    const [y, m, d] = s.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + 1);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
};

export function useIncarichiData() {
    const { addToast } = useToast();

    // Dati principali
    const [incarichi, setIncarichi] = useState<Incarico[]>([]);
    const [turni, setTurni] = useState<any[]>([]);
    const [viewMode, setViewMode] = useState<'OGGI' | 'FUTURI' | 'STORICO' | 'DA_ACCETTARE'>('OGGI');
    const [activeIncaricoId, setActiveIncaricoId] = useState<number | null>(null);
    const [activeTurnoId, setActiveTurnoId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Upload: avanzamento globale
    const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: number }>({});
    const [uploadQueue, setUploadQueue] = useState({ isUploading: false, total: 0, completed: 0 });
    const [fotoDevice, setFotoDevice] = useState('SMARTPHONE');
    const [videoDevice, setVideoDevice] = useState('SMARTPHONE');

    // Upload: modale di conferma prima di partire
    const [pendingUploadItems, setPendingUploadItems] = useState<UploadQueueItem[]>([]);
    const [isPendingConfirmOpen, setIsPendingConfirmOpen] = useState(false);

    // Upload: modale di avanzamento
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
    const [uploadSuccessStep, setUploadSuccessStep] = useState(false);

    // Upload: modale data mancante
    const [missingDateQueue, setMissingDateQueue] = useState<{ file: File; category: string; preview: string }[]>([]);
    const [currentMissingDateIndex, setCurrentMissingDateIndex] = useState(0);
    const [manualFileDate, setManualFileDate] = useState('');

    const pollingInterval = useRef<NodeJS.Timeout | null>(null);
    const _now = new Date();
    const todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

    // Helper puri
    const formatDateTime = (dateStr: string) => {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const getPos = (): Promise<GeolocationPosition> =>
        new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 5000 }));

    // Categorizzazione
    const getIncaricoCategory = useCallback((inc: Incarico): 'OGGI' | 'FUTURI' | 'STORICO' => {
        if (inc.stato === 'COMPLETATO') return 'STORICO';
        // Un urgente senza mandato resta in OGGI anche dopo la dataFine solo se ha una
        // sessione chiusa, così si può ancora inviare il report. Se non è mai stato avviato
        // non serve tenerlo: lo chiude autoRevocaUrgentiOrfaniVuoti.
        const orphanHasClosedSession = ((inc as any).sessioni || []).some((s: any) => !!s.endTime);
        const isOrphanPending = (inc as any).tipoOrigine === 'URGENTE_SENZA_MANDATO'
            && (!inc.relazioni || inc.relazioni.length === 0)
            && orphanHasClosedSession;
        if (isOrphanPending) return 'OGGI';
        // Sessione ancora aperta: resta in OGGI per poter fare Stop dopo mezzanotte.
        const hasOpenSession = (inc.sessioni || []).some((s: any) => s.startTime && !s.endTime);
        if (hasOpenSession) return 'OGGI';
        const startStr = inc.dataInizio ? inc.dataInizio.split('T')[0] : '';
        const endStr = inc.dataFine ? inc.dataFine.split('T')[0] : '';
        // Giornata conclusa con almeno una sessione chiusa ma report non ancora inviato:
        // resta in OGGI per visibilità. Lo Start resta comunque bloccato da isStoricoActive.
        if (endStr && endStr < todayStr) {
            const hasClosedSession = (inc.sessioni || []).some((s: any) => !!s.endTime);
            const noReportYet = !inc.relazioni || inc.relazioni.length === 0;
            const notTerminal = inc.stato !== 'REVOCATO' && inc.stato !== 'RIFIUTATO';
            if (hasClosedSession && noReportYet && notTerminal) return 'OGGI';
            return 'STORICO';
        }
        if (startStr && startStr > todayStr) return 'FUTURI';
        return 'OGGI';
    }, [todayStr]);

    const getTurnoCategory = useCallback((t: any): 'OGGI' | 'FUTURI' | 'STORICO' => {
        const isCompleted = !!t.effettivoInizio && !!t.effettivoFine;
        const reportSent = !!t.reportTrasmesso;
        const isNotturno = !!t.notturno;
        const turnoDateStr = t.dataTurno ? t.dataTurno.split('T')[0] : '';
        // Turno notturno: la fine è il giorno dopo dataTurno, la finestra OGGI si allunga di un giorno.
        const finalDayStr = (isNotturno && turnoDateStr) ? addOneDayStr(turnoDateStr) : turnoDateStr;
        // Turno con sessione ancora aperta: resta in OGGI per poter fare Stop dopo mezzanotte.
        const hasOpenTurnoSession = !!t.effettivoInizio && !t.effettivoFine;
        if (hasOpenTurnoSession) return 'OGGI';
        // Turno notturno senza report: resta in OGGI fino alle 23:59 del giorno di fine,
        // così si possono fare più pause a cavallo della mezzanotte. I turni diurni il
        // giorno dopo possono solo inviare il report.
        if (isNotturno && !reportSent && turnoDateStr && turnoDateStr <= todayStr && todayStr <= finalDayStr) return 'OGGI';
        // Turno fermato ma senza report: resta in OGGI finché il report non viene inviato,
        // altrimenti finirebbe nello storico in sola lettura senza modo di inviarlo.
        // Stessa regola dell'app e di findOperativiOggi lato server.
        if (isCompleted && !reportSent) return 'OGGI';
        // Storico: giornata di fine passata, oppure turno chiuso con report inviato.
        // Senza la seconda condizione lo stop di un turno di oggi lo spostava subito nello storico.
        if (finalDayStr && finalDayStr < todayStr) return 'STORICO';
        if (isCompleted && reportSent) return 'STORICO';
        if (turnoDateStr && turnoDateStr > todayStr) return 'FUTURI';
        return 'OGGI';
    }, [todayStr]);

    // Liste filtrate. Gli autogenerati sono già esclusi dal backend.

    // DA_ACCETTARE non dipende dalle date: contiene tutto ciò che è ancora ASSEGNATO
    // (per i turni statoAccettazione, esclusi quelli revocati).
    const filteredIncarichi = useMemo(() => {
        const base = incarichi;
        if (viewMode === 'DA_ACCETTARE') {
            return base
                .filter(inc => inc.stato === 'ASSEGNATO')
                .sort((a, b) => {
                    const tA = a.dataInizio ? new Date(a.dataInizio).getTime() : 0;
                    const tB = b.dataInizio ? new Date(b.dataInizio).getTime() : 0;
                    return tA - tB;
                });
        }
        return base
            .filter(inc => getIncaricoCategory(inc) === viewMode)
            .sort((a, b) => {
                const tA = a.dataInizio ? new Date(a.dataInizio).getTime() : 0;
                const tB = b.dataInizio ? new Date(b.dataInizio).getTime() : 0;
                return viewMode === 'STORICO' ? tB - tA : tA - tB;
            });
    }, [incarichi, viewMode, getIncaricoCategory]);

    const filteredTurni = useMemo(() => {
        if (viewMode === 'DA_ACCETTARE') {
            return turni
                .filter(t => t.statoAccettazione === 'ASSEGNATO' && !t.dataRevoca)
                .sort((a, b) => {
                    const tA = new Date(`${a.dataTurno?.split('T')[0]}T${a.oraInizio || '00:00:00'}`).getTime();
                    const tB = new Date(`${b.dataTurno?.split('T')[0]}T${b.oraInizio || '00:00:00'}`).getTime();
                    return tA - tB;
                });
        }
        return turni
            .filter(t => getTurnoCategory(t) === viewMode)
            .sort((a, b) => {
                const tA = new Date(`${a.dataTurno?.split('T')[0]}T${a.oraInizio || '00:00:00'}`).getTime();
                const tB = new Date(`${b.dataTurno?.split('T')[0]}T${b.oraInizio || '00:00:00'}`).getTime();
                return viewMode === 'STORICO' ? tB - tA : tA - tB;
            });
    }, [turni, viewMode, getTurnoCategory]);

    // Incarichi conclusi senza relazione, per il promemoria all'apertura di /incarichi.
    // Esclusi revocati/rifiutati, gli ASSEGNATO mai accettati (lo scheduler li rifiuta a breve)
    // e quelli senza nessuna sessione chiusa, per cui il report verrebbe comunque respinto.
    const incarichiSenzaReport = useMemo(() =>
            incarichi
                .filter(inc => inc.stato !== 'REVOCATO' && inc.stato !== 'RIFIUTATO' && inc.stato !== 'ASSEGNATO')
                // Giornata conclusa: si guarda la data e non la categoria, perché ora restano in OGGI.
                .filter(inc => { const e = inc.dataFine ? inc.dataFine.split('T')[0] : ''; return !!e && e < todayStr; })
                .filter(inc => !inc.relazioni || inc.relazioni.length === 0)
                .filter(inc => (inc.sessioni || []).some(s => !!s.endTime))
                .sort((a, b) => {
                    const tA = a.dataInizio ? new Date(a.dataInizio).getTime() : 0;
                    const tB = b.dataInizio ? new Date(b.dataInizio).getTime() : 0;
                    return tB - tA;
                }),
        [incarichi, todayStr]
    );

    // Turni conclusi senza report e non più riprendibili (passato dataTurno, +1 se notturno):
    // stesso promemoria degli incarichi. Finché il turno è in pausa non lo si sollecita.
    const turniSenzaReport = useMemo(() =>
            turni
                .filter(t => {
                    if (!(t.effettivoInizio && t.effettivoFine) || t.reportTrasmesso) return false;
                    const turnoDateStr = t.dataTurno ? t.dataTurno.split('T')[0] : '';
                    if (!turnoDateStr) return false;
                    const finalDayStr = t.notturno ? addOneDayStr(turnoDateStr) : turnoDateStr;
                    return finalDayStr < todayStr;
                })
                .sort((a, b) => {
                    const tA = a.dataTurno ? new Date(a.dataTurno).getTime() : 0;
                    const tB = b.dataTurno ? new Date(b.dataTurno).getTime() : 0;
                    return tB - tA;
                }),
        [turni, todayStr]
    );

    const activeIncarico = useMemo(() => incarichi.find(i => i.id === activeIncaricoId) || null, [incarichi, activeIncaricoId]);
    const activeTurno = useMemo(() => turni.find(t => t.id === activeTurnoId) || null, [turni, activeTurnoId]);

    // Sola lettura (Start bloccato), indipendente dalla categoria di elenco: giornata
    // passata e nessuna sessione aperta. Il report resta comunque inviabile e una sessione
    // aperta a cavallo della mezzanotte si può ancora fermare. COMPLETATO è sempre in sola lettura.
    const isStoricoActive = useMemo(() => {
        if (!activeIncarico) return false;
        if (activeIncarico.stato === 'COMPLETATO') return true;
        const hasOpenSession = (activeIncarico.sessioni || []).some((s: any) => s.startTime && !s.endTime);
        if (hasOpenSession) return false;
        const endStr = activeIncarico.dataFine ? activeIncarico.dataFine.split('T')[0] : '';
        return !!endStr && endStr < todayStr;
    }, [activeIncarico, todayStr]);

    // Incarico che inizia in un giorno futuro: Start bloccato finché non arriva
    // (il backend risponde 409). Si usa dataInizio, come fa il server.
    const isFuturoActive = useMemo(() => {
        if (!activeIncarico) return false;
        if (activeIncarico.stato === 'COMPLETATO') return false;
        const hasOpenSession = (activeIncarico.sessioni || []).some((s: any) => s.startTime && !s.endTime);
        if (hasOpenSession) return false;
        const startStr = activeIncarico.dataInizio ? activeIncarico.dataInizio.split('T')[0] : '';
        return !!startStr && startStr > todayStr;
    }, [activeIncarico, todayStr]);

    const pendingEvidence = useMemo(() => {
        if (!activeIncarico || isStoricoActive) return { hasPending: false, sessions: 0 };
        const reportedIds = new Set(activeIncarico.relazioni?.flatMap((r: any) => r.sessionIds || []) || []);
        const unsent = (activeIncarico.sessioni || []).filter((s: any) => s.endTime && !reportedIds.has(s.id));
        return { hasPending: unsent.length > 0, sessions: unsent.length };
    }, [activeIncarico, isStoricoActive]);

    // Caricamento
    const loadData = useCallback(async (isBackground = false) => {
        if (!isBackground) setLoading(true);
        try {
            const [incarichiRes, turniRes] = await Promise.all([
                IncaricoService.getMyIncarichi(),
                ServizioFiduciarioService.getMyTurni().catch(() => ({ data: [] })),
            ]);
            setIncarichi(Array.isArray(incarichiRes) ? incarichiRes : (incarichiRes as any).data || []);
            setTurni((turniRes as any).data || turniRes);
        } catch (error) {
            console.error(error);
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    // Polling dei media in elaborazione. Un giro alla volta: il successivo parte solo a
    // risposta arrivata, quindi niente richieste in parallelo. Con la scheda nascosta il
    // giro si salta e dopo PROCESSING_POLL_MAX_MS ci si ferma (il pulsante aggiorna resta).
    // L'effetto dipende da hasProcessing e non dai dati: ogni ricarica non lo riavvia.
    const hasProcessing = useMemo(() => !!activeIncarico?.media?.some(
        (m: any) => m.status === 'PROCESSING' || (!m.finalUrl && m.status !== 'ERROR')
    ), [activeIncarico]);
    useEffect(() => {
        if (!hasProcessing) return;
        let cancelled = false;
        const startedAt = Date.now();
        const tick = async () => {
            if (cancelled) return;
            if (Date.now() - startedAt > PROCESSING_POLL_MAX_MS) { pollingInterval.current = null; return; }
            if (!document.hidden) await loadData(true);
            if (cancelled) return;
            pollingInterval.current = setTimeout(tick, PROCESSING_POLL_MS);
        };
        pollingInterval.current = setTimeout(tick, PROCESSING_POLL_MS);
        return () => {
            cancelled = true;
            if (pollingInterval.current) clearTimeout(pollingInterval.current);
            pollingInterval.current = null;
        };
    }, [hasProcessing, loadData]);

    // Fine coda upload
    useEffect(() => {
        if (uploadQueue.isUploading && uploadQueue.total > 0 && uploadQueue.completed >= uploadQueue.total) {
            setUploadQueue({ isUploading: false, total: 0, completed: 0 });
            setUploadSuccessStep(true);
            loadData(true);
        }
    }, [uploadQueue.completed, uploadQueue.total, uploadQueue.isUploading, loadData]);

    // URL S3 aggiornati
    const getFreshUrl = async (item: any): Promise<string> => {
        try {
            if (item.isFieldMedia) {
                return await IncaricoService.getMediaUrl(item.id);
            }
            const res: any = await FilesService.getDownloadUrl(item.id);
            const payload = res?.data || res;
            return payload?.data || payload?.url || (typeof payload === 'string' ? payload : item.urlOriginal);
        } catch {
            return item.urlOriginal || item.finalUrl;
        }
    };

    // Validazione data file
    const isDateValidForIncarico = useCallback((date: Date) => {
        if (!activeIncarico?.dataInizio) return true;
        const start = new Date(activeIncarico.dataInizio);
        start.setHours(0, 0, 0, 0);
        const end = activeIncarico.dataFine ? new Date(activeIncarico.dataFine) : new Date();
        end.setHours(23, 59, 59, 999);
        return date >= start && date <= end;
    }, [activeIncarico]);

    // Upload del singolo file
    const executeSingleUpload = async (file: File, category: string, formattedDate: string) => {
        // Niente GPS sulle prove: la posizione non serve e la sua ricerca ritardava l'upload (0 = non inviata).
        const lat = 0, lon = 0;
        const uid = Math.random().toString(36).substring(2);
        const lastDot = file.name.lastIndexOf('.');
        const base = lastDot !== -1 ? file.name.substring(0, lastDot) : file.name;
        const ext = lastDot !== -1 ? file.name.substring(lastDot) : '';
        const renamed = new File([file], `[${category}] ${base}${ext}`, { type: file.type });

        setUploadingFiles(p => ({ ...p, [renamed.name]: 0 }));
        try {
            await IncaricoService.uploadMediaChunked(
                activeIncarico!.id, renamed, lat, lon, uid,
                (pct: number) => {
                    setUploadingFiles(p => ({ ...p, [renamed.name]: pct }));
                    // Avanzamento globale, approssimato sul file corrente
                    setUploadProgress(prev => Math.max(prev, pct));
                },
                formattedDate
            );
        } catch (err) {
            console.error(`Errore caricamento ${renamed.name}`, err);
        } finally {
            setUploadingFiles(p => { const n = { ...p }; delete n[renamed.name]; return n; });
            setUploadQueue(q => ({ ...q, completed: q.completed + 1 }));
        }
    };

    // Passo 1: selezione file e modale di conferma
    const handleFilesMultiUpload = async (e: React.ChangeEvent<HTMLInputElement>, deviceCategory: string) => {
        if (!activeIncarico || !e.target.files?.length) return;
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (files.length > 500) { addToast('Seleziona massimo 500 file alla volta.', 'error'); return; }

        const items: UploadQueueItem[] = files.map(f => ({
            file: f,
            category: deviceCategory,
            preview: URL.createObjectURL(f),
        }));

        setPendingUploadItems(items);
        setIsPendingConfirmOpen(true);
    };

    // Passo 2: "Continua" → lettura EXIF
    const confirmAndStartUpload = async () => {
        setIsPendingConfirmOpen(false);
        setUploadProgress(0);
        setUploadSuccessStep(false);
        setIsProgressModalOpen(true);

        const ready: { file: File; date: string; category: string }[] = [];
        const needsDate: { file: File; category: string; preview: string }[] = [];

        for (const item of pendingUploadItems) {
            const fileDate = new Date(item.file.lastModified);
            const hasExif = await checkExifDate(item.file);

            if (hasExif || isDateValidForIncarico(fileDate)) {
                ready.push({ file: item.file, date: fileDate.toISOString(), category: item.category });
            } else {
                needsDate.push({ file: item.file, category: item.category, preview: item.preview });
            }
        }

        setUploadQueue(prev => ({ isUploading: true, total: pendingUploadItems.length, completed: prev.completed }));

        if (ready.length > 0) {
            const LIMIT = 3;
            let idx = 0;
            const worker = async () => {
                while (idx < ready.length) {
                    const item = ready[idx++];
                    await executeSingleUpload(item.file, item.category, item.date);
                }
            };
            await Promise.all(Array.from({ length: Math.min(LIMIT, ready.length) }, worker));
        }

        if (needsDate.length > 0) {
            setMissingDateQueue(prev => [...prev, ...needsDate]);
        }
    };

    // Passo 3: data inserita a mano per i file senza EXIF
    const handleManualDateSubmit = () => {
        if (!manualFileDate) { addToast('Inserisci una data valida', 'error'); return; }
        const item = missingDateQueue[currentMissingDateIndex];
        const d = new Date(manualFileDate);
        const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
        executeSingleUpload(item.file, item.category, formatted);
        const isLast = currentMissingDateIndex === missingDateQueue.length - 1;
        if (isLast) { setMissingDateQueue([]); setCurrentMissingDateIndex(0); }
        else setCurrentMissingDateIndex(p => p + 1);
        setManualFileDate('');
    };

    // Accettazione incarico: senza, il collaboratore non può avviare le sessioni.
    const handleAccettaIncarico = async () => {
        if (!activeIncarico) return;
        setActionLoading(true);
        try {
            await safeAction(
                { targetAction: 'INCARICO_ACCETTATO', entityType: 'incarico', entityId: activeIncarico.id },
                () => IncaricoService.accetta(activeIncarico.id),
            );
            addToast('Incarico accettato', 'success');
            await loadData(true);
        } catch (err) {
            console.error(err);
            addToast("Errore nell'accettazione dell'incarico", 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Rifiuto incarico con motivo opzionale: passa a RIFIUTATO e l'admin può riassegnarlo.
    const handleRifiutaIncarico = async (motivo?: string) => {
        if (!activeIncarico) return;
        setActionLoading(true);
        try {
            await safeAction(
                { targetAction: 'INCARICO_RIFIUTATO', entityType: 'incarico', entityId: activeIncarico.id, payload: { motivo } },
                () => IncaricoService.rifiuta(activeIncarico.id, motivo),
            );
            addToast('Incarico rifiutato', 'success');
            // L'incarico non è più operativo: si torna alla lista.
            setActiveIncaricoId(null);
            await loadData(true);
        } catch (err) {
            console.error(err);
            addToast("Errore nel rifiuto dell'incarico", 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Apre un incarico urgente da quello corrente (bloccato perché il report di oggi
    // è già stato inviato) e lo seleziona subito, per poter fare Start senza passare dalla lista.
    const handleCreateUrgenteFromBlocked = async () => {
        if (!activeIncarico) return;
        setActionLoading(true);
        try {
            const res = await IncaricoService.createUrgenteFrom(activeIncarico.id);
            const newId = res?.data?.id;
            addToast('Incarico urgente avviato — sei IN_CORSO', 'success');
            await loadData(true);
            if (newId) setActiveIncaricoId(newId);
        } catch (err: any) {
            console.error(err);
            const msg = err?.response?.data?.message || "Errore nell'apertura dell'urgenza";
            addToast(msg, 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Avvio sessione con foto km
    const handleStartWithPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!activeIncarico || !e.target.files?.length || isStoricoActive) return;
        // Report di oggi già inviato: la UI nasconde il pulsante, ma il controllo qui evita race (es. due tab).
        if (activeIncarico.reportInviatoOggi) {
            addToast("Report di oggi già inviato. Apri un Incarico Urgente per continuare.", 'error');
            return;
        }
        const file = e.target.files[0];
        setActionLoading(true);
        try {
            // Valida l'EXIF prima dello start e dell'upload: se il backend risponde 422 non si parte.
            // Il controllo durante l'upload arriverebbe a sessione già aperta.
            try {
                await IncaricoService.validateKmPhoto(activeIncarico.id, file, new Date().toISOString());
            } catch (vErr: any) {
                const attemptId = clientErrorLogger.generateAttemptId();
                captureClientError({
                    targetAction: 'INCARICO_START',
                    outcome: 'FAIL',
                    attemptId,
                    entityType: 'incarico',
                    entityId: activeIncarico.id,
                    httpStatus: vErr?.response?.status ?? null,
                    errorMsg: `validateKmPhoto: ${vErr?.code || ''} ${vErr?.message || 'unknown'}`.trim(),
                });
                if (vErr?.response?.status === 422) {
                    const data = vErr?.response?.data;
                    const msg = (typeof data?.message === 'string' && data.message)
                        || (typeof data?.error === 'string' && data.error)
                        || "Foto km non valida: la foto deve essere recente e contenere i dati EXIF originali (camera o galleria).";
                    addToast(msg, 'error');
                    return;
                }
                addToast("Errore", 'error');
                return;
            }
            let lat = 0, lon = 0;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch { /* no GPS */ }
            await safeAction(
                { targetAction: 'INCARICO_START', entityType: 'incarico', entityId: activeIncarico.id, payload: { lat, lon } },
                () => IncaricoService.start(activeIncarico.id, lat, lon),
            );
            const uid = Math.random().toString(36).substring(2);
            const ext = file.name.split('.').pop() || 'jpg';
            const renamed = new File([file], `Km_Iniziali_${Date.now()}.${ext}`, { type: file.type });
            setUploadingFiles(prev => ({ ...prev, [renamed.name]: 0 }));
            IncaricoService.uploadMediaChunked(activeIncarico.id, renamed, lat, lon, uid,
                (pct: number) => setUploadingFiles(prev => ({ ...prev, [renamed.name]: pct }))
            )
                .then(() => { setTimeout(() => { setUploadingFiles(prev => { const n = { ...prev }; delete n[renamed.name]; return n; }); loadData(true); }, 1000); })
                .catch((upErr: any) => {
                    setUploadingFiles(prev => { const n = { ...prev }; delete n[renamed.name]; return n; });
                    // Foto km rifiutata (EXIF assente o fuori tolleranza): la sessione è già partita, va fermata e rifatta la foto.
                    if (upErr?.response?.status === 422) {
                        const data = upErr?.response?.data;
                        const msg = (typeof data?.message === 'string' && data.message)
                            || (typeof data?.error === 'string' && data.error)
                            || "Foto km non valida: la foto deve essere recente e contenere i dati EXIF originali (camera o galleria). Ferma la sessione e riavvia.";
                        addToast(msg, 'error');
                    }
                });
            addToast('Sessione Operativa Avviata', 'success');
            await loadData(true);
        } catch (err: any) {
            // 409 con messaggio descrittivo se il report di oggi è già stato inviato.
            const msg = err?.response?.data?.message || "Errore all'avvio";
            addToast(msg, 'error');
            // Ricarica, nel caso il report sia stato inviato da un altro dispositivo.
            if (err?.response?.status === 409) await loadData(true);
        }
        finally { setActionLoading(false); e.target.value = ''; }
    };

    // Chiusura sessione con foto km
    const handleStopWithPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const activeSid = (activeIncarico as any)?.activeSessionId;
        if (!activeSid || !e.target.files?.length || isStoricoActive) return;
        const file = e.target.files[0];
        setActionLoading(true);
        try {
            // Valida l'EXIF prima dello stop: con 422 la sessione resta aperta.
            try {
                await IncaricoService.validateKmPhoto(activeIncarico!.id, file, new Date().toISOString());
            } catch (vErr: any) {
                const attemptId = clientErrorLogger.generateAttemptId();
                captureClientError({
                    targetAction: 'INCARICO_STOP',
                    outcome: 'FAIL',
                    attemptId,
                    entityType: 'workSession',
                    entityId: (activeIncarico as any)?.activeSessionId,
                    httpStatus: vErr?.response?.status ?? null,
                    errorMsg: `validateKmPhoto: ${vErr?.code || ''} ${vErr?.message || 'unknown'}`.trim(),
                });
                if (vErr?.response?.status === 422) {
                    const data = vErr?.response?.data;
                    const msg = (typeof data?.message === 'string' && data.message)
                        || (typeof data?.error === 'string' && data.error)
                        || "Foto km finale non valida: la foto deve essere recente e contenere i dati EXIF originali (camera o galleria).";
                    addToast(msg, 'error');
                    return;
                }
                addToast("Errore", 'error');
                return;
            }
            let lat = 0, lon = 0;
            try { const p = await getPos(); lat = p.coords.latitude; lon = p.coords.longitude; } catch { /* no GPS */ }
            await safeAction(
                { targetAction: 'INCARICO_STOP', entityType: 'workSession', entityId: activeSid, payload: { lat, lon, incaricoId: activeIncarico!.id } },
                () => IncaricoService.stop(activeSid, lat, lon),
            );
            const uid = Math.random().toString(36).substring(2);
            const ext = file.name.split('.').pop() || 'jpg';
            const renamed = new File([file], `Km_Finali_${Date.now()}.${ext}`, { type: file.type });
            setUploadingFiles(prev => ({ ...prev, [renamed.name]: 0 }));
            IncaricoService.uploadMediaChunked(activeIncarico!.id, renamed, lat, lon, uid,
                (pct: number) => setUploadingFiles(prev => ({ ...prev, [renamed.name]: pct }))
            )
                .then(() => { setTimeout(() => { setUploadingFiles(prev => { const n = { ...prev }; delete n[renamed.name]; return n; }); loadData(true); }, 1000); })
                .catch((upErr: any) => {
                    setUploadingFiles(prev => { const n = { ...prev }; delete n[renamed.name]; return n; });
                    // Foto km rifiutata: la sessione è già chiusa, va ricaricata la foto.
                    if (upErr?.response?.status === 422) {
                        const data = upErr?.response?.data;
                        const msg = (typeof data?.message === 'string' && data.message)
                            || (typeof data?.error === 'string' && data.error)
                            || "Foto km finale non valida: la foto deve essere recente e contenere i dati EXIF originali (camera o galleria).";
                        addToast(msg, 'error');
                    }
                });
            addToast('Sessione Terminata', 'success');
            await loadData(true);
        } catch { addToast('Errore di chiusura', 'error'); }
        finally { setActionLoading(false); e.target.value = ''; }
    };

    return {
        // Stato
        incarichi, turni, viewMode, setViewMode,
        activeIncaricoId, setActiveIncaricoId,
        activeTurnoId, setActiveTurnoId,
        loading, actionLoading,
        uploadingFiles, uploadQueue, setUploadQueue,
        fotoDevice, setFotoDevice,
        videoDevice, setVideoDevice,
        // Modale conferma upload
        pendingUploadItems, setPendingUploadItems,
        isPendingConfirmOpen, setIsPendingConfirmOpen,
        // Modale progresso upload
        uploadProgress, isProgressModalOpen, setIsProgressModalOpen,
        uploadSuccessStep, setUploadSuccessStep,
        // Data mancante
        missingDateQueue, setMissingDateQueue,
        currentMissingDateIndex, setCurrentMissingDateIndex,
        manualFileDate, setManualFileDate,
        pollingInterval,
        // Calcolati
        todayStr, activeIncarico, activeTurno,
        filteredIncarichi, filteredTurni,
        incarichiSenzaReport,
        turniSenzaReport,
        isStoricoActive, isFuturoActive, pendingEvidence,
        // Funzioni
        loadData, getIncaricoCategory, getTurnoCategory,
        getPos, getFreshUrl, formatDateTime,
        isDateValidForIncarico, executeSingleUpload,
        handleFilesMultiUpload, confirmAndStartUpload,
        handleManualDateSubmit,
        handleStartWithPhoto, handleStopWithPhoto,
        handleAccettaIncarico, handleRifiutaIncarico,
        handleCreateUrgenteFromBlocked,
    };
}